import { getGameState } from '../systems/state.js';
import { getDiscoveryMap, getDiscoveryCount } from './discovery.js';
import { eventBus, GameEvents } from '../systems/events.js';

let journalButton = null;
let journalOverlay = null;
let isOpen = false;

// Category definitions for the grid
const CATEGORIES = {
  creatures: {
    label: 'Creatures',
    keys: ['crab', 'dolphin', 'seagull', 'sea_turtle'],
  },
  objects: {
    label: 'Objects',
    keys: ['treasure_chest', 'palmtree', 'welcome_sign', 'beer_bottle', 'beach_bum'],
  },
  bonuses: {
    label: 'Bonuses',
    keys: [
      'crab_wave', 'crab_stampede', 'barrel_roll', 'show_off',
      'synchronized_splash', 'formation_flight', 'golden_gull',
      'music_note_catch', 'melody_maker', 'cheers', 'recycler',
      'guitar_solo', 'jam_session',
    ],
  },
  secrets: {
    label: 'Secrets',
    keys: ['buried_seashell', 'tide_pool', 'message_in_bottle', 'hermit_crab'],
  },
  combos: {
    label: 'Combinations',
    keys: ['crab_orchestra', 'dolphin_serenade'],
  },
};

function injectStyles() {
  const style = document.createElement('style');
  style.textContent = `
    #journal-btn {
      position: fixed;
      top: 16px;
      right: 16px;
      z-index: 900;
      background: linear-gradient(135deg, #f5e6c8 0%, #e8d5a3 100%);
      border: 2px solid #c4a265;
      border-radius: 12px;
      padding: 8px 14px;
      font-family: 'Georgia', serif;
      font-size: 15px;
      color: #5a3e1b;
      cursor: pointer;
      box-shadow: 0 2px 10px rgba(0, 0, 0, 0.15);
      display: flex;
      align-items: center;
      gap: 6px;
      transition: transform 0.2s ease, box-shadow 0.2s ease;
      user-select: none;
      -webkit-tap-highlight-color: transparent;
    }
    #journal-btn:hover {
      transform: scale(1.05);
      box-shadow: 0 4px 15px rgba(0, 0, 0, 0.25);
    }
    #journal-btn:active {
      transform: scale(0.97);
    }

    #journal-overlay {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      z-index: 950;
      background: rgba(0, 0, 0, 0.6);
      backdrop-filter: blur(4px);
      -webkit-backdrop-filter: blur(4px);
      display: flex;
      justify-content: center;
      align-items: center;
      opacity: 0;
      pointer-events: none;
      transition: opacity 0.3s ease;
    }
    #journal-overlay.open {
      opacity: 1;
      pointer-events: auto;
    }

    #journal-panel {
      background: linear-gradient(145deg, #faf0d7 0%, #f0deb0 50%, #e8d5a3 100%);
      border: 3px solid #c4a265;
      border-radius: 20px;
      width: 90%;
      max-width: 520px;
      max-height: 80vh;
      overflow-y: auto;
      padding: 28px 24px;
      box-shadow: 0 10px 40px rgba(0, 0, 0, 0.4);
      font-family: 'Georgia', serif;
      color: #5a3e1b;
      transform: scale(0.9);
      transition: transform 0.3s ease;
    }
    #journal-overlay.open #journal-panel {
      transform: scale(1);
    }

    .journal-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 20px;
      border-bottom: 2px solid #c4a265;
      padding-bottom: 12px;
    }
    .journal-title {
      font-size: 24px;
      font-weight: bold;
      display: flex;
      align-items: center;
      gap: 8px;
    }
    .journal-close {
      background: none;
      border: 2px solid #c4a265;
      border-radius: 50%;
      width: 36px;
      height: 36px;
      font-size: 20px;
      color: #5a3e1b;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: background 0.2s;
      font-family: 'Georgia', serif;
    }
    .journal-close:hover {
      background: #c4a265;
      color: #fff;
    }

    .journal-category {
      margin-bottom: 20px;
    }
    .journal-category-label {
      font-size: 16px;
      font-weight: bold;
      margin-bottom: 10px;
      opacity: 0.8;
      text-transform: uppercase;
      letter-spacing: 1px;
    }
    .journal-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 10px;
    }
    @media (max-width: 480px) {
      .journal-grid {
        grid-template-columns: repeat(2, 1fr);
      }
      #journal-panel {
        padding: 20px 16px;
      }
    }

    .journal-card {
      background: linear-gradient(135deg, #fff8e7 0%, #f5e6c8 100%);
      border: 2px solid #d4b87a;
      border-radius: 12px;
      padding: 14px 10px;
      text-align: center;
      min-height: 90px;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 6px;
      transition: transform 0.2s;
    }
    .journal-card.discovered {
      border-color: #a0853a;
    }
    .journal-card.discovered.new-glow {
      box-shadow: 0 0 12px rgba(255, 200, 50, 0.6);
      animation: glow-pulse 1.5s ease-in-out 2;
    }
    @keyframes glow-pulse {
      0%, 100% { box-shadow: 0 0 12px rgba(255, 200, 50, 0.3); }
      50% { box-shadow: 0 0 20px rgba(255, 200, 50, 0.7); }
    }

    .journal-card-icon {
      font-size: 32px;
    }
    .journal-card-icon.undiscovered {
      filter: brightness(0) opacity(0.3);
      font-size: 28px;
    }
    .journal-card-name {
      font-size: 13px;
      font-weight: bold;
      line-height: 1.2;
    }
    .journal-card-name.undiscovered {
      color: #a08a6a;
      font-style: italic;
    }
  `;
  document.head.appendChild(style);
}

function createJournalButton() {
  journalButton = document.createElement('button');
  journalButton.id = 'journal-btn';
  updateButtonBadge();
  journalButton.addEventListener('click', toggleJournal);
  document.body.appendChild(journalButton);
}

function updateButtonBadge() {
  if (!journalButton) return;
  const { discovered, total } = getDiscoveryCount();
  journalButton.innerHTML = `\uD83D\uDCD6 <span>${discovered}/${total}</span>`;
}

function createJournalOverlay() {
  journalOverlay = document.createElement('div');
  journalOverlay.id = 'journal-overlay';
  journalOverlay.addEventListener('click', (e) => {
    // Close when clicking the backdrop (outside the panel)
    if (e.target === journalOverlay) closeJournal();
  });
  document.body.appendChild(journalOverlay);
}

function renderJournalContent() {
  const state = getGameState();
  const discoveryMap = getDiscoveryMap();
  const { discovered, total } = getDiscoveryCount();

  // Track which discoveries are new since last journal open
  const lastOpenTime = journalOverlay.dataset.lastOpenTime
    ? parseInt(journalOverlay.dataset.lastOpenTime, 10)
    : 0;

  let html = `
    <div id="journal-panel">
      <div class="journal-header">
        <div class="journal-title">\uD83D\uDCD6 Discovery Journal</div>
        <button class="journal-close" aria-label="Close">\u00D7</button>
      </div>
      <div style="text-align: center; margin-bottom: 16px; font-size: 15px; opacity: 0.8;">
        ${discovered} of ${total} discovered
      </div>
  `;

  for (const [catKey, category] of Object.entries(CATEGORIES)) {
    const catDiscovered = category.keys.filter(k => state.discoveries[k]).length;
    html += `
      <div class="journal-category">
        <div class="journal-category-label">${category.label} (${catDiscovered}/${category.keys.length})</div>
        <div class="journal-grid">
    `;

    for (const key of category.keys) {
      const info = discoveryMap[key];
      const disc = state.discoveries[key];
      const isDiscovered = !!disc;
      const isNew = isDiscovered && disc.timestamp > lastOpenTime;

      if (isDiscovered) {
        html += `
          <div class="journal-card discovered${isNew ? ' new-glow' : ''}">
            <div class="journal-card-icon">${info.icon}</div>
            <div class="journal-card-name">${info.name}</div>
          </div>
        `;
      } else {
        html += `
          <div class="journal-card">
            <div class="journal-card-icon undiscovered">${info.icon}</div>
            <div class="journal-card-name undiscovered">???</div>
          </div>
        `;
      }
    }

    html += `
        </div>
      </div>
    `;
  }

  html += `</div>`;
  journalOverlay.innerHTML = html;

  // Attach close button handler
  const closeBtn = journalOverlay.querySelector('.journal-close');
  if (closeBtn) {
    closeBtn.addEventListener('click', closeJournal);
  }
}

function openJournal() {
  if (isOpen) return;
  isOpen = true;
  renderJournalContent();
  journalOverlay.classList.add('open');
}

function closeJournal() {
  if (!isOpen) return;
  isOpen = false;
  journalOverlay.classList.remove('open');
  journalOverlay.dataset.lastOpenTime = Date.now().toString();
}

function toggleJournal() {
  if (isOpen) {
    closeJournal();
  } else {
    openJournal();
  }
}

export function initJournal() {
  injectStyles();
  createJournalButton();
  createJournalOverlay();

  // Update badge when a new discovery is made
  eventBus.on(GameEvents.DISCOVERY_NEW, () => {
    updateButtonBadge();
  });

  // Close journal on Escape key
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && isOpen) {
      closeJournal();
    }
  });

  console.log('[Journal] UI initialized');
}
